// third party imports
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "react-feather";

// internal rebet imports
import "./stylesEditUserDataModal.css";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import { handleExpiredTokenError } from "../../../../../utils/ErrorUtils";

// component imports
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { CloseOutlined, UserOutlined } from "@ant-design/icons";
import { DatePicker, Input, message } from "antd";
import CustomInput from "../../../../CommonComponents/CustomInput/CustomInput";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';

dayjs.extend(customParseFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);

// asset imports

const EditUserDataModal = forwardRef(({ data, handleCancel, refreshUserData }, ref) => {
  // non-state variables
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );
  const navigate = useNavigate();

  // state variables
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [bio, setBio] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [isApiRunning, setIsApiRunning] = useState(false);

  // use effects'
  useEffect(() => {
    setFirstName(data?.first_name);
    setLastName(data?.last_name);
    setBio(data?.bio);
    setPhoneNumber(data?.phone_number);
    setDateOfBirth(data?.date_of_birth);
  }, [data]);


  const clearFormData = () => {
    setFirstName(data?.first_name);
    setLastName(data?.last_name);
    setBio(data?.bio);
    setPhoneNumber(data?.phone_number);
    setDateOfBirth(data?.date_of_birth);
    setIsApiRunning(false);
  };

  useImperativeHandle(ref, () => ({
    clearFormData,
  }));

  // api functions
  const editUserData = async () => {
    setIsApiRunning(true);


    const params = {
      user_id: data?.user_id,
      first_name: firstName,
      last_name: lastName,
      bio: bio,
      phone_number: phoneNumber,
      date_of_birth: dayjs(dateOfBirth)?.format("YYYY-MM-DD"),
    };


    try {
      const response = await PrivateAdminPanelInstance?.put(
        API_ROUTES?.EDIT_USER_PROFILE?.replace("{user_id}", data?.user_id),
        params
      );

      await refreshUserData({ ...params });
      message.success(response?.data?.message);
    } catch (error) {
      message.error(error?.response?.data?.message || AppStrings.SOMETHING_WENT_WRONG);
    } finally {
      setIsApiRunning(false);
      handleCancel();
    }
  };

  // helper functions
  const handleChangeFirstName = (event) => {
    setFirstName(event?.target?.value);
  };

  const handleChangeLastName = (event) => {
    setLastName(event?.target?.value);
  };

  const handleChangeBio = (event) => {
    setBio(event?.target?.value);
  };

  const handleChangePhoneNumber = (event) => {
    setPhoneNumber(event?.target?.value);
  };

  const handleChangeDateOfBirth = (date) => {
    // DatePicker passes a dayjs object, convert to string for consistency
    setDateOfBirth(date ? date.format("YYYY-MM-DD") : null);
  };

  const handleEditUserDataPressed = () => {
    editUserData();
  };

  return (
    <div className="edit-user-data-modal-container">
      <div className="edit-user-data-modal-content">
        <div className="modal-header">
          <div className="modal-header-title">
            <UserOutlined className="modal-header-title-icon" />
            <h2 className="modal-title"> {AppStrings.EDIT_USER}</h2>
          </div>
          <CloseOutlined
            onClick={handleCancel}
            className="close-icon"
            data-testid="edit-user-data-modal-close"
          />
        </div>

        <div className="divider" />
        <div className="edit-user-data-row">
          <div className="edit-user-data-item">
            <label className="edit-user-data-label">
              {AppStrings.FIRST_NAME}
            </label>
            <CustomInput
              type="text"
              placeholder={AppStrings.FIRST_NAME}
              className="edit-user-data-input"
              value={firstName}
              onChange={handleChangeFirstName}
              prefix=" "
            />
          </div>

          <div className="edit-user-data-item">
            <label className="edit-user-data-label">
              {AppStrings.LAST_NAME}
            </label>
            <CustomInput
              type="text"
              placeholder={AppStrings.LAST_NAME}
              className="edit-user-data-input"
              value={lastName}
              onChange={handleChangeLastName}
              prefix=" "
            />
          </div>
        </div>

        <div className="edit-user-data-row">
          <div className="edit-user-data-item">
            <label className="edit-user-data-label">{AppStrings.BIO}</label>
            <CustomInput
              type="text"
              placeholder={AppStrings.BIO}
              className="edit-user-data-input"
              value={bio}
              onChange={handleChangeBio}
              prefix=" "
            />
          </div>

          <div className="edit-user-data-item">
            <label className="edit-user-data-label">{AppStrings.DATE_OF_BIRTH}</label>
            <DatePicker
              placeholder={AppStrings.DATE_OF_BIRTH} 
              value={dateOfBirth ? dayjs(dateOfBirth, "YYYY-MM-DD") : null}
              onChange={handleChangeDateOfBirth} 
              className="edit-user-date-of-birth-input" 
              format={{
                format: 'YYYY-MM-DD',
                type: 'mask',
              }}
            />
          </div>

          <div className="edit-user-data-item">
            <label className="edit-user-data-label">
              {AppStrings.PHONE_NUMBER}
            </label>
            <CustomInput
              type="text"
              placeholder={AppStrings.PHONE_NUMBER}
              className="edit-user-data-input"
              value={phoneNumber}
              onChange={handleChangePhoneNumber}
              prefix=" "
            />
          </div>
        </div>

        <div className="divider" />

        <div className="buttons">
          <button
            className="cancel-button"
            onClick={handleCancel}
            disabled={isApiRunning}
          >
            {AppStrings.CLOSE}
          </button>
          <button
            className="save-button"
            onClick={handleEditUserDataPressed}
            disabled={isApiRunning}
          >
            {isApiRunning ? (
              <RebetLoader height={"20px"} width={"20px"} />
            ) : (
              AppStrings.SAVE
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

export default EditUserDataModal;
